import type { TranslationLookup } from "./createT";

/** Deduplicates runtime lookups and exposes stable immutable snapshots to external-store subscribers. */
export class TranslationLookupRegistry {
  private readonly lookups = new Map<string, TranslationLookup>();
  private readonly listeners = new Set<() => void>();
  private snapshot: readonly TranslationLookup[] = [];
  private notifyQueued = false;

  /** Records the latest value for a requested/resolved key pair and queues one subscriber notification. */
  readonly record = (lookup: TranslationLookup): void => {
    const identity = `${lookup.key}\u0000${lookup.resolvedKey}`;
    const previous = this.lookups.get(identity);
    if (
      previous?.language === lookup.language &&
      previous.template === lookup.template &&
      previous.value === lookup.value
    ) {
      return;
    }

    this.lookups.set(identity, lookup);
    this.snapshot = [...this.lookups.values()].sort(
      (left, right) => left.key.localeCompare(right.key) || left.resolvedKey.localeCompare(right.resolvedKey),
    );
    if (this.notifyQueued) {
      return;
    }
    this.notifyQueued = true;
    queueMicrotask(() => {
      this.notifyQueued = false;
      this.listeners.forEach((listener) => listener());
    });
  };

  /** Subscribes to snapshot changes and returns an unsubscribe function. */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => this.listeners.delete(listener);
  };

  /** Returns the same sorted snapshot identity until a recorded lookup changes. */
  readonly getSnapshot = (): readonly TranslationLookup[] => this.snapshot;
}
